import { useState } from 'react';
import { Building2, CreditCard, Webhook, Server, Workflow, BadgeCheck, KeyRound, MapPin, ShieldCheck } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { CompanyInfoForm } from '@/components/settings/CompanyInfoForm';
import { PaymentMethodsForm } from '@/components/settings/PaymentMethodsForm';
import { WhatsAppCloudConfigForm } from '@/components/settings/WhatsAppCloudConfigForm';
import { N8nConfigForm } from '@/components/settings/N8nConfigForm';
import { HermesConfigForm } from '@/components/settings/HermesConfigForm';
import { OpenClawConfigForm } from '@/components/settings/OpenClawConfigForm';
import { ApprovalPinForm } from '@/components/settings/ApprovalPinForm';
import { RayonForm } from '@/components/settings/RayonForm';
import { ApprovalRulesForm } from '@/components/settings/ApprovalRulesForm';
import { cn } from '@/lib/format';

type Tab = 'company' | 'payment' | 'pin' | 'rayon' | 'approval' | 'cloud' | 'n8n' | 'hermes' | 'openclaw';

const TABS: { value: Tab; label: string; icon: typeof Building2; description: string }[] = [
  {
    value: 'company',
    label: 'Perusahaan',
    icon: Building2,
    description: 'Nama, alamat, logo, NPWP, default PPN',
  },
  {
    value: 'payment',
    label: 'Payment',
    icon: CreditCard,
    description: 'Metode pembayaran yang diterima',
  },
  {
    value: 'pin',
    label: 'PIN persetujuan',
    icon: KeyRound,
    description: 'PIN owner untuk menyetujui void di kasir',
  },
  {
    value: 'rayon',
    label: 'Rayon',
    icon: MapPin,
    description: 'Wilayah untuk membatasi akses per rayon',
  },
  {
    value: 'approval',
    label: 'Persetujuan',
    icon: ShieldCheck,
    description: 'Aksi yang butuh approval, dan levelnya',
  },
  {
    value: 'cloud',
    label: 'WhatsApp resmi',
    icon: BadgeCheck,
    description: 'Meta Cloud API — recommended, tanpa VPS',
  },
  {
    value: 'n8n',
    label: 'n8n',
    icon: Workflow,
    description: 'Self-hosted via n8n + Baileys',
  },
  {
    value: 'hermes',
    label: 'Hermes',
    icon: Server,
    description: 'Direct Baileys webhook — dipakai kalau n8n nonaktif',
  },
  {
    value: 'openclaw',
    label: 'OpenClaw',
    icon: Webhook,
    description: 'Legacy: third-party WhatsApp gateway',
  },
];

export default function Settings() {
  const [tab, setTab] = useState<Tab>('company');

  return (
    <>
      <PageHeader
        overline="Owner only"
        title="Settings"
        description="Konfigurasi company, payment methods, dan WhatsApp gateway (n8n / Hermes / OpenClaw)."
      />

      <div className="grid lg:grid-cols-[240px_1fr] gap-6">
        {/* Tab nav (vertical) */}
        <nav className="space-y-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.value;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => setTab(t.value)}
                className={cn(
                  'w-full text-left px-4 py-3 rounded-lg transition-colors',
                  active
                    ? 'bg-primary/10 text-primary border border-primary/20'
                    : 'hover:bg-line/30 text-ink-subtle hover:text-ink border border-transparent',
                )}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Icon size={16} />
                  <span className="text-small font-medium">{t.label}</span>
                </div>
                <p className="text-caption text-ink-subtle pl-6">{t.description}</p>
              </button>
            );
          })}
        </nav>

        {/* Content */}
        <div>
          {tab === 'company' && <CompanyInfoForm />}
          {tab === 'payment' && <PaymentMethodsForm />}
          {tab === 'pin' && <ApprovalPinForm />}
          {tab === 'rayon' && <RayonForm />}
          {tab === 'approval' && <ApprovalRulesForm />}
          {tab === 'cloud' && <WhatsAppCloudConfigForm />}
          {tab === 'n8n' && <N8nConfigForm />}
          {tab === 'hermes' && <HermesConfigForm />}
          {tab === 'openclaw' && <OpenClawConfigForm />}
        </div>
      </div>
    </>
  );
}
